/// <amd-module name="Repos/Discussion/Editor/Integrations/DiffSummaryDiscussionIntegration" />
import * as React from "react";
import { DiffViewerOrientation, DiffViewerSide } from "Repos/Common/Contracts/VersionControlViewModel";
import {
    DiffSummaryDiscussionWidgetIds,
    IDiffSummaryDiscussionIntegration,
    IDiffSummaryDiscussionWidget,
    IDiscussionThreadModel,
    ILineInfo,
    LineInfoType,
    SpecialCommentPositions
} from "Repos/Discussion/Common/DiscussionContracts";
import { createDiffId } from "Repos/Changes/Components/Diff/DiffBlock";
import { ObservableValue } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";
import { SizeObserver } from "VSSUI/SizeObserver";
import { AddCommentButton } from "../Components/AddCommentButton";
import { IDiscussionBaseWidgetProps, IDiscussionWidgetContentProps } from "../Widgets/DiscussionBaseWidget";
import { DiscussionSummaryWidget, IDiscussionSummaryWidgetProps } from "../Widgets/DiscussionSummaryWidget";
import { BaseDiscussionIntegration, IDiscussionIntegrationProps } from "./BaseDiscussionIntegration";

export interface IDiffSummaryDiscussionIntegrationProps extends IDiscussionIntegrationProps {
    /**
     * Determines whether this discussion integration is bound to a specific change or root.
     */
    isRoot?: boolean;
}

export class DiffSummaryDiscussionIntegration
    extends BaseDiscussionIntegration<IDiffSummaryDiscussionWidget, IDiffSummaryDiscussionIntegrationProps>
    implements IDiffSummaryDiscussionIntegration {
    private modifiedToDisplayLineMapping: { [key: number]: number | undefined } = {};
    private originalToDisplayLineMapping: { [key: number]: number | undefined } = {};

    constructor(props: IDiffSummaryDiscussionIntegrationProps) {
        super(props);

        if (props.isRoot) {
            // No need to have line information since it is not bound to a file
            this.initialize();
        }
    }

    public getDisplayLineNumber(actualLineNumber: number, side: DiffViewerSide): number {
        if (actualLineNumber === SpecialCommentPositions.File) {
            return SpecialCommentPositions.File;
        }

        const displayLineNumber =
            side === DiffViewerSide.Original
                ? this.originalToDisplayLineMapping[actualLineNumber]
                : this.modifiedToDisplayLineMapping[actualLineNumber];

        return displayLineNumber != undefined ? displayLineNumber : SpecialCommentPositions.NotVisible;
    }

    public initializeLineMapping(originalToDisplay: { [key: number]: number }, modifiedToDisplay: { [key: number]: number }): void {
        this.originalToDisplayLineMapping = originalToDisplay;
        this.modifiedToDisplayLineMapping = modifiedToDisplay;
        this.initialize();
    }

    public renderAddCommentWidget(lineInfo: ILineInfo, addToOriginalDiff: boolean, className?: string): JSX.Element {
        return (
            <AddCommentButton
                className={className}
                onClick={event => {
                    const { lineInfo: selectionLineInfo, isOriginalSide: selectionIsOriginalSide } = getLineInfoFromSelection(window.getSelection());
                    if (selectionLineInfo && intersectsLineInfo(selectionLineInfo, lineInfo)) {
                        this.onStartThread(selectionLineInfo, selectionIsOriginalSide);
                    } else {
                        this.onStartThread(lineInfo, addToOriginalDiff);
                    }

                    event.stopPropagation();
                    event.preventDefault();
                }}
            />
        );
    }

    public addComment(lineInfo: ILineInfo, addToOriginalDiff: boolean): void {
        return this.onStartThread(lineInfo, addToOriginalDiff);
    }

    protected addThreadToWidget(thread: IDiscussionThreadModel) {
        // Will add any filler widgets as necessary by calling createWidget
        super.addThreadToWidget(thread);

        const integrationContext = this.props.getIntegrationContext(thread);
        const { addToOriginalDiff, lineInfo } = integrationContext;
        const displayLineNumber = lineInfo
            ? this.getDisplayLineNumber(lineInfo.lineEnd, addToOriginalDiff ? DiffViewerSide.Original : DiffViewerSide.Modified)
            : SpecialCommentPositions.File;

        if (displayLineNumber !== SpecialCommentPositions.NotVisible) {
            const fillerWidget = this.widgets.value.filter(
                w => this.isMyWidgetInstance(w) && w.belongsToLine(displayLineNumber, !addToOriginalDiff, LineInfoType.Display)
            )[0];

            fillerWidget && fillerWidget.addThread(thread);
        }
    }

    protected onRemoveThread = (threadId: number): void => {
        if (this.props) {
            const threads = this.props.discussionStore.getDiscussionThreads();
            const thread = this.filterThreads(threads.value).filter(t => t.id == threadId)[0];
            this.props.discussionStore.removeThread(threadId);
            if (thread) {
                this.onThreadManuallyDeleted(thread);
            }
        }  
    };

    protected isMyWidgetInstance(widget: IDiffSummaryDiscussionWidget): boolean {
        return widget instanceof DiscussionSummaryWidget;
    }
    
    protected onThreadManuallyDeleted(thread: IDiscussionThreadModel): void {
        const { filePath, getIntegrationContext } = this.props;
        const integrationContext = getIntegrationContext(thread);
        const { addToOriginalDiff, lineInfo } = integrationContext;
        const line = lineInfo?.lineStart ?? 0;
        const fileName = filePath?.split('/').pop() ?? "";
        const id = addToOriginalDiff ? createDiffId(fileName, line, undefined) : createDiffId(fileName, undefined, line);
        document?.getElementById(id)?.focus();
    }

    protected createWidget(widgetProps: IDiscussionBaseWidgetProps<IDiscussionWidgetContentProps>): DiscussionSummaryWidget {
        // Create a corresponding filler widget for all new widgets
        const fillerHeight = new ObservableValue<number>(0);
        const updateFillerHeight = (width: number, height: number) => {
            fillerHeight.value = height;
        };

        const side = widgetProps.addToOriginalDiff ? DiffViewerSide.Original : DiffViewerSide.Modified;
        const displayLineInfo = this.getDisplayLineInfo(widgetProps.lineInfo, side);

        // Need to wrap the original render in a sizeObserver to keep them the same height
        const mutatedWidgetProps: IDiscussionSummaryWidgetProps = {
            ...widgetProps,
            id: DiffSummaryDiscussionWidgetIds.Comment,
            lineInfo: displayLineInfo,
            lookupDisplayLineNumber: lineNumber => this.getDisplayLineNumber(lineNumber, side) || 0,
            renderContent: props => <SizeObserver onSizeChange={updateFillerHeight}>{widgetProps.renderContent(props)}</SizeObserver>
        };

        if (
            displayLineInfo &&
            displayLineInfo.lineStart !== SpecialCommentPositions.NotVisible &&
            displayLineInfo.lineStart !== SpecialCommentPositions.File
        ) {
            const fillerWidgetProps: IDiscussionSummaryWidgetProps = {
                addToOriginalDiff: !mutatedWidgetProps.addToOriginalDiff,
                hideOnOrientations: [DiffViewerOrientation.Inline],
                id: DiffSummaryDiscussionWidgetIds.Filler,
                lineInfo: mutatedWidgetProps.lineInfo,
                lookupDisplayLineNumber: mutatedWidgetProps.lookupDisplayLineNumber,
                pageContext: this.props.pageContext,
                renderContent: () => (
                    <Observer height={fillerHeight}>
                        {(observerProps: { height: number }) => <div className="filler" style={{ height: `${observerProps.height}px` }} />}
                    </Observer>
                )
            };

            this.widgets.push(new DiscussionSummaryWidget(fillerWidgetProps));
        }

        // Construct the actual widget
        return new DiscussionSummaryWidget(mutatedWidgetProps);
    }

    private getDisplayLineInfo(fileLineInfo: ILineInfo | undefined, side: DiffViewerSide): ILineInfo | undefined {
        if (fileLineInfo) {
            return {
                ...fileLineInfo,
                lineEnd: this.getDisplayLineNumber(fileLineInfo.lineEnd, side),
                lineStart: this.getDisplayLineNumber(fileLineInfo.lineStart, side),
                type: LineInfoType.Display
            };
        }

        return undefined;
    }
}

function getLineInfoFromSelection(selection: Selection | null): { lineInfo: ILineInfo | undefined; isOriginalSide: boolean } {
    if (selection && selection.anchorNode && selection.focusNode) {
        const { mLine: startMLine, oLine: startOLine, offset: startOffset } = extractLineDataFromElement(selection.anchorNode);
        const { mLine: endMLine, oLine: endOLine, offset: endOffset } = extractLineDataFromElement(selection.focusNode);

        const lineStartOffset = (startOffset ? parseInt(startOffset) : 0) + selection.anchorOffset + 1;
        const lineEndOffset = (endOffset ? parseInt(endOffset) : 0) + selection.focusOffset + 1;

        if (startMLine && endMLine) {
            return buildLineInfoAndSideResult(parseInt(startMLine), parseInt(endMLine), lineStartOffset, lineEndOffset, false);
        } else if (startOLine && endOLine) {
            return buildLineInfoAndSideResult(parseInt(startOLine), parseInt(endOLine), lineStartOffset, lineEndOffset, true);
        }
    }

    return { lineInfo: undefined, isOriginalSide: false };
}

function extractLineDataFromElement(node: Node) {
    const element = node instanceof Element ? node : node.parentElement;

    if (element) {
        const offset = element.getAttribute("data-offset");
        const parentLine = offset ? element.parentElement : element;
        if (parentLine) {
            return {
                mLine: parentLine.getAttribute("data-mline"),
                oLine: parentLine.getAttribute("data-oline"),
                offset
            };
        }
    }

    return {};
}

function buildLineInfoAndSideResult(lineStart: number, lineEnd: number, lineStartOffset: number, lineEndOffset: number, isOriginalSide: boolean) {
    if (lineStart < lineEnd || (lineStart === lineEnd && lineStartOffset < lineEndOffset)) {
        return {
            lineInfo: {
                lineStart,
                lineEnd,
                lineStartOffset,
                lineEndOffset,
                type: LineInfoType.Actual
            },
            isOriginalSide
        };
    } else {
        return {
            lineInfo: {
                lineStart: lineEnd,
                lineEnd: lineStart,
                lineStartOffset: lineEndOffset,
                lineEndOffset: lineStartOffset,
                type: LineInfoType.Actual
            },
            isOriginalSide
        };
    }
}

/**
 * Whether one range lines cover the other. It ignores character offsets.
 */
function intersectsLineInfo(a: ILineInfo, b: ILineInfo): boolean {
    return (a.lineStart >= b.lineStart && a.lineEnd <= b.lineEnd) || (a.lineStart <= b.lineStart && a.lineEnd >= b.lineEnd);
}
